import { AvailableInterviewOptions, Interview, MultiDayOptionGroup, SingleDayOptionGroup } from '../../types';
import { DateTime, Interval } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import {
    getDateShort,
    getDayAbbreviated,
    getDayOfMonthNoPadding,
    getMonthAbbreviated,
    getTotalDaysBetweenTwoDates,
} from '../../utils/date-utils';
import DatePickerOptionToggle from './DatePickerOptionsToggle';
import classnames from 'classnames';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const style = require('./DatePicker.module.scss');

interface DatePickerProps {
    availableOptions: AvailableInterviewOptions;
    maxTimespan?: number;
    primaryColor?: string;
    selectedOptions?: string[];
    onSelect: (selectedDays: Set<string>) => void;
    isMultiDayChecked: boolean;
    handleMultiDayChecked: () => void;
    interview?: Interview;
    selectedLanguage: string;
    hideDatePickerToggle?: boolean;
}

interface Calendar {
    dateShort: string;
    dayNum: string;
    dayString: string;
    month: string;
    style: {
        dayCard: {
            [key: string]: boolean;
        };
        dayOfWeekStr: {
            [key: string]: boolean;
        };
        dayOfWeekNum: {
            [key: string]: boolean;
        };
    };
    index: number;
}

interface OptionsMap {
    [key: string]: string[];
}

const LIMIT = 15;
const STANDARD_NUMBER_OF_DAYS = 21;
const MEDIUM_WIDTH = 844;

const DatePicker = ({
    availableOptions,
    maxTimespan = 45,
    primaryColor,
    selectedOptions,
    onSelect,
    isMultiDayChecked,
    handleMultiDayChecked,
    interview,
    selectedLanguage,
    hideDatePickerToggle,
}: DatePickerProps) => {
    const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set([]));
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [calendar, setCalendar] = useState<Calendar[]>([]);
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
    const [limit, setLimit] = useState<number>(window.innerWidth <= MEDIUM_WIDTH ? maxTimespan : LIMIT);

    const buildMap = (options: SingleDayOptionGroup) => {
        const map: OptionsMap = {};
        options.forEach((singleDayOptions, date) => {
            singleDayOptions.forEach((singleDayOption) => {
                if (Object.prototype.hasOwnProperty.call(map, date)) {
                    map[date]?.push(singleDayOption.hash);
                } else {
                    map[date] = [singleDayOption.hash];
                }
            });
            if (interview?.settings?.allowGuestToSuggestTimes && date) {
                map[date] = [];
            }
        });

        return map;
    };

    const buildMultiDayMap = (options: MultiDayOptionGroup) => {
        const map: OptionsMap = {};
        options.forEach((multiDayOptions) => {
            multiDayOptions.forEach((multiDayOption) => {
                multiDayOption.duration.forEach((duration) => {
                    const key = getDateShort(DateTime.fromISO(duration.startTime));
                    if (Object.prototype.hasOwnProperty.call(map, key)) {
                        map[key]?.push(multiDayOption.hash);
                    } else {
                        map[key] = [multiDayOption.hash];
                    }
                });
            });
        });

        return map;
    };

    const buildCalendar = useCallback(
        (singleDay: OptionsMap, multiDay?: OptionsMap) => {
            let dateTime = DateTime.fromISO(DateTime.now().toISO());

            if (interview?.settings.firstDate) {
                dateTime = DateTime.fromISO(interview?.settings.firstDate);
            }

            const range = numberOfDaysToShow;

            return Interval.fromDateTimes(dateTime.startOf('day'), dateTime.endOf('day').plus({ days: range - 1 }))
                .splitBy({ day: 1 })
                .map((d) => d.start)
                .map((d, index) => {
                    const dateShort = getDateShort(d, 'en');
                    const isMultiDay = Boolean(multiDay && multiDay[dateShort]);
                    const isSingleDay = Boolean(singleDay[dateShort]);
                    const isUnavailable = !isSingleDay && !isMultiDay;
                    const isOption = isSingleDay || isMultiDay;
                    const isSuggested = isSingleDay && !availableOptions.singleDay.get(dateShort)?.length;

                    return {
                        dateShort,
                        dayNum: getDayOfMonthNoPadding(d),
                        dayString: getDayAbbreviated(d).slice(0, 2),
                        month: getMonthAbbreviated(d),
                        style: {
                            dayCard: {
                                [style.dayCard]: true,
                                [style.unavailable]: isUnavailable,
                            },
                            dayOfWeekStr: {
                                [style.dayOfWeekStr]: true,
                                [style.unavailable]: isUnavailable,
                            },
                            dayOfWeekNum: {
                                [style.dayOfWeekNum]: true,
                                [style.option]: isOption,
                                [style.singleDay]: isSingleDay,
                                [style.multiDay]: isMultiDay,
                                [style.unavailable]: isUnavailable,
                                [style.isSuggested]: isSuggested,
                            },
                        },
                        index,
                    };
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [availableOptions?.singleDay, availableOptions?.multiDay, maxTimespan],
    );

    useEffect(() => {
        const selectedDaysCopy = new Set(selectedOptions);

        setSelectedDays(selectedDaysCopy);
    }, [selectedOptions]);

    useEffect(() => {
        const singleDay = buildMap(availableOptions.singleDay);

        if (isMultiDayChecked) {
            const singleDay = buildMap(availableOptions.singleDay);
            const multiDay = buildMultiDayMap(availableOptions.multiDay);
            const build = buildCalendar(singleDay, multiDay);
            setCalendar(build);
        } else {
            const build = buildCalendar(singleDay);
            setCalendar(build);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildCalendar, availableOptions, selectedLanguage]);

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-date-picker-color', primaryColor);
        }
    }, [primaryColor]);

    useEffect(() => {
        window.addEventListener('resize', () => {
            setWindowWidth(window.innerWidth);
            if (window.innerWidth <= MEDIUM_WIDTH) {
                setLimit(maxTimespan);
                setActiveIndex(0);
            } else {
                setLimit(LIMIT);
            }
        });

        return () => {
            window.removeEventListener('resize', () => {
                setWindowWidth(window.innerWidth);
                setLimit(LIMIT);
            });
        };
    }, [maxTimespan, windowWidth]);

    const slideRight = () => {
        if (activeIndex < maxTimespan && activeIndex + limit < maxTimespan) {
            setActiveIndex(activeIndex + limit);
        }
    };

    const slideLeft = () => {
        if (activeIndex > 0 && activeIndex - limit >= 0) {
            setActiveIndex(activeIndex - limit);
        }
    };

    const toggleSelected = (date: string) => {
        const selectedDaysCopy = new Set(selectedDays);

        if (!selectedDaysCopy.has(date)) {
            selectedDaysCopy.add(date);
        } else {
            selectedDaysCopy.delete(date);
        }

        setSelectedDays(selectedDaysCopy);

        onSelect(selectedDaysCopy);
    };

    const handleChecked = () => {
        handleMultiDayChecked();
        const singleDay = buildMap(availableOptions?.singleDay);
        const multiDay = buildMultiDayMap(availableOptions?.multiDay);
        const build = buildCalendar(singleDay, multiDay);
        setCalendar(build);
    };

    const calculateDaysRange = (): number => {
        if (interview !== undefined) {
            return getTotalDaysBetweenTwoDates(interview.settings.firstDate, interview.settings.lastDate);
        }
        return 0;
    };
    const daysRange = calculateDaysRange();

    const calculateNumberOfDaysToShow = (): number => {
        if (interview?.settings.allowGuestToSuggestTimes) {
            return daysRange > STANDARD_NUMBER_OF_DAYS ? daysRange : STANDARD_NUMBER_OF_DAYS;
        } else {
            return Math.min(daysRange, maxTimespan);
        }
    };

    const numberOfDaysToShow = calculateNumberOfDaysToShow();

    const leftDisabled = activeIndex < 0 || activeIndex - limit < 0;
    const rightDisabled =
        activeIndex >= maxTimespan || activeIndex + limit >= maxTimespan || activeIndex + limit >= numberOfDaysToShow;
    let prevMonth = '';
    const areArrowsHidden = numberOfDaysToShow <= 15 ? 'none' : 'flex';

    return (
        <div className={style.datePickerContainer}>
            <button
                disabled={leftDisabled}
                className={classnames({
                    [style.disabled]: leftDisabled,
                })}
                onClick={slideLeft}
                style={{
                    display: areArrowsHidden,
                }}
            >
                <FontAwesomeIcon className={style.chevron} icon={faChevronLeft} />
            </button>
            <div className={style.datePicker}>
                <div className={style.scrollContainer}>
                    <div className={style.datePickerRow}>
                        {calendar.slice(activeIndex, activeIndex + limit).map((day: Calendar, index) => {
                            const isSelected = selectedDays.has(day.dateShort);

                            if (index > 0) {
                                prevMonth =
                                    calendar[activeIndex + index - 1]?.month ??
                                    getMonthAbbreviated(DateTime.now().minus({ month: 1 }));
                            }

                            const showMonth = activeIndex === day.index || prevMonth !== day.month;
                            const selectedStyle = {
                                [style.selected]: isSelected,
                            };

                            return (
                                <Fragment key={`day-card-${index}`}>
                                    {index > 0 && prevMonth !== day.month && <div className={style.monthDivider} />}
                                    <div>
                                        <div
                                            className={classnames(style.dayCardMonth, selectedStyle, {
                                                [style.hidden]: !showMonth,
                                            })}
                                        >
                                            {day.month}
                                        </div>
                                        <div
                                            className={classnames(day.style.dayCard, selectedStyle)}
                                            onClick={() => toggleSelected(day.dateShort)}
                                        >
                                            <div className={classnames(day.style.dayOfWeekStr, selectedStyle)}>
                                                {day.dayString}
                                            </div>
                                            <div className={classnames(day.style.dayOfWeekNum, selectedStyle)}>
                                                {day.dayNum}
                                            </div>
                                        </div>
                                    </div>
                                </Fragment>
                            );
                        })}
                    </div>
                </div>
                {!hideDatePickerToggle && (
                    <DatePickerOptionToggle handleChecked={handleChecked} isChecked={isMultiDayChecked} />
                )}
            </div>
            <button
                disabled={rightDisabled}
                className={classnames({
                    [style.disabled]: rightDisabled,
                })}
                onClick={slideRight}
                style={{
                    display: areArrowsHidden,
                }}
            >
                <FontAwesomeIcon className={style.chevron} icon={faChevronRight} />
            </button>
        </div>
    );
};

export default DatePicker;
